import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Star, Loader2 } from 'lucide-react';
import type { CampingSpot, AppUser, CampingRatings } from '../../types';
import { addCampingRating } from '../../services/firebaseService';

interface CampingRatingModalProps {
  spot: CampingSpot;
  user: AppUser;
  onClose: () => void;
}

const CATEGORIES: { key: keyof CampingRatings; labelKey: string; fallback: string }[] = [
  { key: 'renhold',    labelKey: 'camping_rating.renhold',    fallback: 'Renhold' },
  { key: 'fasiliteter', labelKey: 'camping_rating.fasiliteter', fallback: 'Fasiliteter' },
  { key: 'sanitaer',   labelKey: 'camping_rating.sanitaer',   fallback: 'Sanitær / Bad' },
  { key: 'ro',         labelKey: 'camping_rating.ro',         fallback: 'Ro / Støy' },
  { key: 'prisVerdi',  labelKey: 'camping_rating.pris_verdi', fallback: 'Pris / Verdi' },
];

const StarRow: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5 transition-transform active:scale-90"
          aria-label={`${n} stjerner`}
        >
          <Star
            size={24}
            className={`transition-colors ${(hover || value) >= n ? 'fill-amber-400 text-amber-400' : 'text-[var(--border-secondary)]'}`}
          />
        </button>
      ))}
    </div>
  );
};

const CampingRatingModal: React.FC<CampingRatingModalProps> = ({ spot, user, onClose }) => {
  const { t } = useTranslation();
  const [ratings, setRatings] = useState<CampingRatings>({
    renhold: 0, fasiliteter: 0, sanitaer: 0, ro: 0, prisVerdi: 0,
  });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const allRated = Object.values(ratings).every(v => v > 0);

  const handleSubmit = async () => {
    if (!allRated || submitting) return;
    setSubmitting(true);
    try {
      await addCampingRating(spot.id, user, ratings, comment.trim(), spot.name);
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      console.error('CampingRatingModal submit error', e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-[var(--bg-surface)] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[var(--border-primary)]">
          <div>
            <p className="text-2xs font-bold uppercase tracking-widest text-emerald-400">
              {t('camping_rating.heading', 'Vurder campingplassen')}
            </p>
            <p className="text-[13px] font-bold text-[var(--text-primary)] truncate max-w-[220px]">
              {spot.name}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Star size={24} className="fill-emerald-400 text-emerald-400" />
            </div>
            <p className="text-[13px] font-bold text-[var(--text-primary)]">
              {t('camping_rating.thanks', 'Takk for vurderingen!')}
            </p>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            {CATEGORIES.map(({ key, labelKey, fallback }) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <span className="text-[12px] font-semibold text-[var(--text-secondary)] w-28 shrink-0">
                  {t(labelKey, fallback)}
                </span>
                <StarRow value={ratings[key]} onChange={v => setRatings(prev => ({ ...prev, [key]: v }))} />
              </div>
            ))}

            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={t('camping_rating.comment_placeholder', 'Kommentar (valgfritt)...')}
              rows={2}
              maxLength={400}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2 text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none focus:outline-none focus:border-emerald-500/50"
            />

            <button
              onClick={handleSubmit}
              disabled={!allRated || submitting}
              className="w-full py-3 rounded-xl bg-emerald-500 text-white text-[12px] font-black uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              {submitting
                ? <Loader2 size={16} className="animate-spin" />
                : t('camping_rating.submit', 'Send vurdering')}
            </button>

            <button
              onClick={onClose}
              className="w-full py-2 text-tiny text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              {t('camping_rating.skip', 'Hopp over')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampingRatingModal;
